import type { CSSProperties } from 'react';
import type { TagTone } from '@dayon/design-system';
import { formatPesos, formatPesosCompact, formatRate } from '@dayon/money';
import type { AgentRow, Band, PeriodRow, PlanRow } from './types.ts';

/* ── Vocabulary ────────────────────────────────────────────────────────────
   The DB screams; the design writes sentence case. One mapping, so no cell
   invents its own wording. */

export const TIER_LABEL: Record<string, string> = { MASTER: 'Master', SUB_AGENT: 'Sub-agent' };

/**
 * The design shows three statuses (Active / Pending / Suspended); the column
 * holds four. INVITED is a real row an operator can be looking at, so it keeps
 * its own word rather than being disguised as Pending — but it is grouped with
 * PENDING_KYB everywhere a count or a filter is drawn, or an invited agent
 * would be reachable from no branch of the rail at all.
 */
export const STATUS_LABEL: Record<string, string> = {
  INVITED: 'Invited',
  PENDING_KYB: 'Pending',
  ACTIVE: 'Active',
  SUSPENDED: 'Suspended',
};

export const PENDING = new Set(['INVITED', 'PENDING_KYB']);

/**
 * PORTAL access, which is a different axis from the agent's own status and uses
 * two of the same words. An agent whose status is INVITED has not had its KYB
 * recorded; an agent whose PORTAL status is INVITED has been sent a link it has
 * not accepted. The labels are written so the two can never be read as one:
 * this one says "Can sign in", never "Active".
 */
export const PORTAL_STATUS_LABEL: Record<string, string> = {
  NONE: 'No login',
  INVITED: 'Invited',
  ACTIVE: 'Can sign in',
  SUSPENDED: 'Suspended',
};

/**
 * Only "can sign in" is filled. An outstanding invite is work and is outlined,
 * like every other awaiting-something state here; no login at all is quiet,
 * because it is the resting state of an agent nobody has provisioned and a
 * badge on every row would train the eye past it.
 */
export const portalTone = (status: string): TagTone =>
  status === 'ACTIVE' ? 'accent' : status === 'INVITED' ? 'outline' : 'quiet';

export const PERIOD_STATUS_LABEL: Record<string, string> = {
  OPEN: 'Open',
  CALCULATED: 'Calculated',
  PAID: 'Paid',
};

/** "Applies to" — which tier a plan is offered to. */
export const PLAN_TIER_LABEL: Record<string, string> = {
  MASTER: 'Master agents',
  SUB_AGENT: 'Sub-agents',
  ANY: 'Either tier',
};

export const PLAN_KIND_LABEL: Record<string, string> = {
  FLAT: 'Flat rate',
  TIERED: 'Tiered band',
  RETAINER: 'Retainer',
};

export const SETTLEMENT_LABEL: Record<string, string> = {
  WEEKLY: 'Weekly',
  FORTNIGHTLY: 'Fortnightly',
  MONTHLY: 'Monthly',
};

/** Master = filled accent, Sub-agent = neutral. The design's tier chip. */
export const tierTone = (tier: string): TagTone => (tier === 'MASTER' ? 'accent' : 'neutral');

/** Active = filled, awaiting-something = outline, everything else = quiet. */
export const statusTone = (status: string): TagTone =>
  status === 'ACTIVE' ? 'accent' : PENDING.has(status) ? 'outline' : 'quiet';

export const periodTone = (status: string): TagTone =>
  status === 'PAID' ? 'accent' : status === 'CALCULATED' ? 'outline' : 'neutral';

/**
 * The row action label is data-driven and the three labels open the SAME
 * drawer. It is a description of what the agent needs, not three behaviours.
 */
export const actionLabel = (status: string): string =>
  PENDING.has(status) ? 'Approve' : status === 'SUSPENDED' ? 'Review' : 'View';

/* ── Dates ─────────────────────────────────────────────────────────────── */
const day = new Intl.DateTimeFormat('en-PH', { day: 'numeric', month: 'short', year: 'numeric' });
export const stamp = (iso: string): string => day.format(new Date(iso));

export const subLine: CSSProperties = { fontSize: '11px' };
export const captionLine: CSSProperties = { fontSize: '11px', marginTop: 'var(--space-2)' };

/* ── Plan terms, as a sentence ─────────────────────────────────────────── */

/**
 * A plan's rate structure, in words.
 *
 * A tiered plan has no single rate, so it must not be printed as one: "4.0%"
 * over a plan that pays 7% above ₱10M is a figure an agent would budget
 * against. The whole band set is spelled out instead, and the WHOLE-AMOUNT
 * reading is named — crossing the threshold re-prices the entire period rather
 * than only the excess, and the two readings differ by ₱300,000 on an ₱11M
 * period.
 */
export function bandSentence(bands: readonly Band[]): string {
  if (bands.length === 0) return 'no bands recorded';
  const sorted = [...bands].toSorted((a, b) =>
    BigInt(a.minNetRevenue) < BigInt(b.minNetRevenue) ? -1 : 1,
  );
  const first = sorted[0]!;
  if (sorted.length === 1) return `${formatRate(Number(first.rateBp))} of net GGR`;
  return sorted
    .map((b, i) => {
      const rate = formatRate(Number(b.rateBp));
      if (i === 0) {
        return `${rate} under ${formatPesosCompact(BigInt(sorted[1]!.minNetRevenue))}`;
      }
      return `${rate} from ${formatPesosCompact(BigInt(b.minNetRevenue))}`;
    })
    .join(', ');
}

/** The plan's retainer, or null where it has none. */
export function retainerSentence(retainerAmount: string, settlement: string): string | null {
  const amount = BigInt(retainerAmount);
  if (amount === 0n) return null;
  return `${formatPesos(amount)} ${(SETTLEMENT_LABEL[settlement] ?? settlement).toLowerCase()} retainer`;
}

/** The carry-over TERM, which the platform could not state at all before. */
export function carrySentence(carryOverMonths: string): string {
  const months = Number(carryOverMonths);
  if (months === 0) return 'carried until it clears';
  return `carried ${months} month${months === 1 ? '' : 's'}`;
}

/** The plan's entry rate — what an agent on it earns before any threshold. */
export function entryRate(bands: readonly Band[]): number | null {
  if (bands.length === 0) return null;
  const sorted = [...bands].toSorted((a, b) =>
    BigInt(a.minNetRevenue) < BigInt(b.minNetRevenue) ? -1 : 1,
  );
  return Number(sorted[0]!.rateBp);
}

/* ── Derived facts ─────────────────────────────────────────────────────── */

const CADENCE_WORD: Record<string, string> = {
  WEEKLY: 'Weekly',
  FORTNIGHTLY: 'Fortnightly',
  MONTHLY: 'Monthly',
};

/**
 * The settlement cadence of the periods the platform has actually ISSUED.
 *
 * The design writes "Monthly · 5th working day" in three places; the schema
 * comment on commission_period says "Weekly, paid Monday 09:00 PHT". One of
 * them is stale, and an agent reading the wrong one plans their cash flow
 * around a payment that is three weeks away. So this screen prints what the
 * period table says, and reports a PLAN that disagrees rather than believing it.
 *
 * ── IT USED TO MEASURE THE NEWEST PERIOD'S LENGTH, AND THAT BROKE ──────────
 * `commission_period.settlement` did not exist, so the cadence was inferred
 * from `period_end - period_start`. That inference is wrong on exactly one
 * period in the chain's life: when a chain cut on the wrong boundary is snapped
 * back onto the Manila gaming day, the period that does the snapping is SHORT —
 * six days and sixteen hours in the case this platform actually has. Six days
 * is not a cadence anybody offers; it was a one-off correction, and inferring
 * "Monthly" from it would have told every agent the wrong payment date.
 *
 * The period now RECORDS its own cadence, so this reads it. The length is still
 * worth showing where the two disagree, which `shortPeriod` below does.
 */
export function periodCadence(periods: PeriodRow[]): string | null {
  const first = periods[0];
  if (!first) return null;
  if (first.settlement) return CADENCE_WORD[first.settlement] ?? first.settlement;
  // A payload from before the column existed. Measured, as it used to be.
  const days = Math.round(
    (Date.parse(first.period_end) - Date.parse(first.period_start)) / 86_400_000,
  );
  if (days === 7) return 'Weekly';
  if (days === 14) return 'Fortnightly';
  if (days >= 28 && days <= 31) return 'Monthly';
  return `Every ${days} days`;
}

/**
 * A period whose window is SHORTER than its own cadence, and by how much.
 *
 * This is what a boundary correction looks like from the outside: one period
 * that runs from wherever the previous chain ended to the next real gaming-day
 * boundary. It is not an error and it is not hidden — an agent whose statement
 * covers six days rather than seven is entitled to see that said somewhere.
 */
export function shortPeriod(period: PeriodRow): number | null {
  const hours = Math.round(
    (Date.parse(period.period_end) - Date.parse(period.period_start)) / 3_600_000,
  );
  const full =
    period.settlement === 'FORTNIGHTLY' ? 336 : period.settlement === 'MONTHLY' ? 0 : 168;
  // A month is not a fixed number of hours, so a monthly period is never
  // reported as short: there is nothing to compare it against.
  if (full === 0 || hours >= full) return null;
  return hours;
}

/** The plan settlement word that matches a measured cadence, for comparison. */
export const cadenceKey = (cadence: string | null): string | null =>
  cadence === 'Weekly'
    ? 'WEEKLY'
    : cadence === 'Fortnightly'
      ? 'FORTNIGHTLY'
      : cadence === 'Monthly'
        ? 'MONTHLY'
        : null;

/** A master's branch is the master plus its direct sub-agents. Three levels
    means there is no deeper tier to walk, so this needs no recursion. */
export function inBranch(branch: string, a: AgentRow): boolean {
  if (branch === 'all') return true;
  if (branch === 'sub') return a.tier === 'SUB_AGENT';
  if (branch === 'pending') return PENDING.has(a.status);
  if (branch === 'susp') return a.status === 'SUSPENDED';
  if (!branch.startsWith('master:')) return true;
  const ref = branch.slice('master:'.length);
  return a.ref === ref || a.parent_ref === ref;
}

/** Whether a plan may be offered to an agent of this tier. */
export const planFitsTier = (plan: PlanRow, tier: string): boolean =>
  plan.tier === 'ANY' || plan.tier === tier;
